/**
 * Kreator wniosku urlopowego portalu (spec 03 §4) — wspólny dla zakładki
 * „Urlopy" i kafelków urlopowych w „Wnioskach" (fala 8, grupa 40).
 *
 * Kafelek urlopu składał dawniej ogólny wniosek z datami: bez puli, bez dni
 * roboczych, a po zatwierdzeniu bez wpisu w „Moich nieobecnościach". Teraz
 * obie drogi otwierają ten sam kreator — saldo i dni robocze liczy serwer, a
 * każdy kod odmowy ma własne zdanie. Kreator sam pobiera salda (ten sam klucz
 * zapytania co zakładka), więc z kafelka działa tak samo jak z zakładki.
 *
 * Przy umowie cywilnej urlop nie przysługuje (art. 152 § 1 k.p.), a po ustaniu
 * zatrudnienia urlopu się nie udziela (art. 171 § 1 k.p.) — wtedy kreator
 * mówi to wprost i nie ma przycisku złożenia.
 */
import { useId, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, FormField, InfoBanner, Input, Modal, Select, Spinner, useToast } from '@k1p4/ui'
import { formatDate } from '@k1p4/i18n'
import { ApiError, api } from '../../api/client'
import { leaveErrorKey } from './leave-errors'
import styles from './portal.module.css'

export interface LeaveRequestModalProps {
  open: boolean
  onClose: () => void
  /** Typ urlopu wybrany z kafelka; bez niego — wypoczynkowy. */
  initialTypeKey?: string
}

const EMPTY = { dateFrom: '', dateTo: '', note: '' }

export function LeaveRequestModal({ open, onClose, initialTypeKey }: LeaveRequestModalProps) {
  const { t, i18n } = useTranslation()
  const toast = useToast()
  const queryClient = useQueryClient()
  const ids = { type: useId(), from: useId(), to: useId(), note: useId() }

  const [typeKey, setTypeKey] = useState<string | null>(null)
  const [form, setForm] = useState(EMPTY)
  const [error, setError] = useState<string | null>(null)

  const leaves = useQuery({ queryKey: ['me', 'leaves'], queryFn: () => api.meLeaves(), enabled: open })
  const balances = leaves.data?.balances
  /* Kreator proponuje wyłącznie typy z pulą — reszta wymaga rozmowy z HR. */
  const selectableTypes = useMemo(
    () => (balances ?? []).filter((balance) => balance.limitDays !== null),
    [balances],
  )
  const selected = typeKey ?? initialTypeKey ?? 'wypoczynkowy'

  function close(): void {
    setTypeKey(null)
    setForm(EMPTY)
    setError(null)
    onClose()
  }

  const submit = useMutation({
    mutationFn: () =>
      api.meSubmitLeave({
        typeKey: selected,
        dateFrom: form.dateFrom,
        dateTo: form.dateTo,
        ...(form.note.trim() ? { note: form.note.trim() } : {}),
      }),
    onSuccess: () => {
      toast.show(t('portal.leaves.submitted'), { tone: 'success' })
      void queryClient.invalidateQueries({ queryKey: ['me', 'leaves'] })
      void queryClient.invalidateQueries({ queryKey: ['me', 'dashboard'] })
      void queryClient.invalidateQueries({ queryKey: ['my-requests'] })
      close()
    },
    onError: (caught) => {
      /* Każdy kod odmowy ma własne zdanie — także „nie dotyczy" i „poza zatrudnieniem". */
      setError(leaveErrorKey(caught instanceof ApiError ? caught.code : null))
    },
  })

  const notApplicable = leaves.data?.leaveEntitlement === 'nie_dotyczy'
  const employment = leaves.data?.employment
  const ended = employment?.ended === true
  const noRecord = leaves.data !== undefined && !leaves.data.hasEmployeeRecord
  const canRequest = leaves.data !== undefined && !noRecord && !notApplicable && !ended
  const rangeReady = form.dateFrom !== '' && form.dateTo !== '' && form.dateTo >= form.dateFrom

  return (
    <Modal
      open={open}
      onClose={close}
      title={t('portal.leaves.formTitle')}
      footer={
        <div className={styles.modalActions}>
          <Button variant="secondary" onClick={close}>
            {t('portal.leaves.cancel')}
          </Button>
          <Button
            disabled={!canRequest || !rangeReady || submit.isPending}
            onClick={() => submit.mutate()}
          >
            {submit.isPending ? (
              <Spinner label={t('portal.leaves.submitting')} />
            ) : (
              t('portal.leaves.submit')
            )}
          </Button>
        </div>
      }
    >
      {leaves.isPending ? <Spinner label={t('common.loading')} /> : null}
      {noRecord ? <InfoBanner tone="info">{t('portal.noRecord')}</InfoBanner> : null}
      {notApplicable ? <InfoBanner tone="info">{t('portal.leaves.notApplicable')}</InfoBanner> : null}
      {ended ? (
        <InfoBanner tone="info">
          {employment?.endDate
            ? t('portal.leaves.employmentEnded', { date: formatDate(employment.endDate, 'dot') })
            : t('portal.leaves.employmentEndedNoDate')}
        </InfoBanner>
      ) : null}
      {error !== null ? <InfoBanner tone="danger">{t(error)}</InfoBanner> : null}
      {canRequest ? (
        <>
          <div className={styles.formGrid}>
            <FormField className={styles.formFull} label={t('portal.leaves.type')} htmlFor={ids.type}>
              <Select id={ids.type} value={selected} onChange={(event) => setTypeKey(event.target.value)}>
                {selectableTypes.map((balance) => (
                  <option key={balance.typeKey} value={balance.typeKey}>
                    {i18n.language.startsWith('pl') ? balance.labelPl : balance.labelEn}
                  </option>
                ))}
              </Select>
            </FormField>
            <FormField label={t('portal.leaves.dateFrom')} htmlFor={ids.from}>
              <Input
                id={ids.from}
                type="date"
                value={form.dateFrom}
                onChange={(event) => setForm({ ...form, dateFrom: event.target.value })}
              />
            </FormField>
            <FormField label={t('portal.leaves.dateTo')} htmlFor={ids.to}>
              <Input
                id={ids.to}
                type="date"
                value={form.dateTo}
                onChange={(event) => setForm({ ...form, dateTo: event.target.value })}
              />
            </FormField>
            <FormField className={styles.formFull} label={t('portal.leaves.note')} htmlFor={ids.note}>
              <textarea
                id={ids.note}
                className={styles.textarea}
                rows={3}
                placeholder={t('portal.leaves.notePlaceholder')}
                value={form.note}
                onChange={(event) => setForm({ ...form, note: event.target.value })}
              />
            </FormField>
          </div>
          {/* Dokładną liczbę dni roboczych policzy serwer — tu tylko zapowiedź. */}
          <p className={styles.formNote}>{t('portal.leaves.ctaBody')}</p>
        </>
      ) : null}
    </Modal>
  )
}
